import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronDown, Feather, Pause, Play, Video } from "lucide-react";
import Reveal from "./Reveal";
import Modal from "./Modal";
import { bookColors, poems, songs } from "../../data/content";
import { goToVideoPoem } from "../../lib/scroll";

const FEATURED_SONG_IDS = ["calma", "recordar"];
const featuredSongs = FEATURED_SONG_IDS.map((id) => songs.find((s) => s.id === id)).filter(Boolean);
const restSongs = songs.filter((s) => !FEATURED_SONG_IDS.includes(s.id));

const fmt = (s) => {
    if (!s || !isFinite(s)) return "--:--";
    const m = Math.floor(s / 60);
    return `${m}:${String(Math.floor(s % 60)).padStart(2, "0")}`;
};

const SongRow = ({ song, index, playing, onToggle, onEnded, register, onReadPoem }) => {
    const audioEl = useRef(null);
    const [cur, setCur] = useState(0);
    const [dur, setDur] = useState(0);
    const progress = dur ? (cur / dur) * 100 : 0;

    const seek = (e) => {
        const a = audioEl.current;
        if (!a || !dur) return;
        const rect = e.currentTarget.getBoundingClientRect();
        a.currentTime = Math.min(Math.max((e.clientX - rect.left) / rect.width, 0), 1) * dur;
    };

    return (
        <div
            data-testid={`song-row-${index}`}
            className="border-t border-line last:border-b py-5 sm:py-6 flex items-center gap-4 sm:gap-6"
        >
            <audio
                ref={(el) => {
                    audioEl.current = el;
                    register(el);
                }}
                src={song.src}
                preload="metadata"
                onLoadedMetadata={(e) => setDur(e.target.duration)}
                onTimeUpdate={(e) => setCur(e.target.currentTime)}
                onEnded={() => {
                    setCur(0);
                    onEnded();
                }}
            />
            <button
                data-testid={`song-toggle-${index}`}
                onClick={onToggle}
                aria-label={playing ? `Pausar ${song.title}` : `Reproducir ${song.title}`}
                className={`shrink-0 w-12 h-12 sm:w-14 sm:h-14 rounded-full border flex items-center justify-center transition-colors duration-300 ${
                    playing
                        ? "bg-crimson border-crimson text-paper"
                        : "border-ink/40 text-ink hover:border-crimson hover:text-crimson"
                }`}
            >
                {playing ? <Pause size={18} /> : <Play size={18} className="ml-0.5" />}
            </button>
            <div className="flex-1 min-w-0">
                <div className="flex items-baseline justify-between gap-4">
                    <h3
                        data-testid={`song-title-${index}`}
                        className={`font-display text-xl sm:text-2xl truncate transition-colors duration-300 ${
                            playing ? "text-crimson italic" : "text-ink"
                        }`}
                    >
                        {song.title}
                    </h3>
                    <span
                        data-testid={`song-time-${index}`}
                        className="shrink-0 font-mono text-[10px] sm:text-[11px] tracking-[0.12em] text-subtle"
                    >
                        {fmt(cur)} / {fmt(dur)}
                    </span>
                </div>
                <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1">
                    <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-subtle">
                        {song.note}
                    </p>
                    {song.poemId && (
                        <button
                            data-testid={`song-poem-link-${index}`}
                            onClick={onReadPoem}
                            className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.18em] text-crimson hover:text-ink transition-colors"
                        >
                            <Feather size={11} />
                            Leer el poema
                        </button>
                    )}
                </div>
                <div
                    data-testid={`song-progress-${index}`}
                    onClick={seek}
                    role="progressbar"
                    aria-valuenow={Math.round(progress)}
                    aria-valuemin={0}
                    aria-valuemax={100}
                    className="mt-3 h-[3px] w-full bg-line cursor-pointer"
                >
                    <div
                        className="h-full bg-crimson transition-[width] duration-200"
                        style={{ width: `${progress}%` }}
                    />
                </div>
            </div>
        </div>
    );
};

const Songs = () => {
    const [playingId, setPlayingId] = useState(null);
    const [poemActive, setPoemActive] = useState(null);
    const [expanded, setExpanded] = useState(false);
    const players = useRef({});

    const toggleExpanded = () => {
        if (expanded) {
            Object.values(players.current).forEach((el) => el && el.pause());
            setPlayingId(null);
        }
        setExpanded(!expanded);
    };

    const toggle = (song) => {
        const a = players.current[song.id];
        if (!a) return;
        if (playingId === song.id) {
            a.pause();
            setPlayingId(null);
            return;
        }
        Object.entries(players.current).forEach(([id, el]) => {
            if (id !== song.id && el) el.pause();
        });
        a.play().catch(() => {});
        setPlayingId(song.id);
    };

    const openPoem = (song) => {
        const poem = poems.find((p) => p.id === song.poemId);
        if (poem) setPoemActive(poem);
    };

    const playSong = (song) => {
        const isRest = restSongs.some((s) => s.id === song.id);
        if (isRest) setExpanded(true);
        setTimeout(
            () => {
                Object.values(players.current).forEach((el) => el && el.pause());
                const a = players.current[song.id];
                if (!a) return;
                a.currentTime = 0;
                a.play().catch(() => {});
                setPlayingId(song.id);
                const row = document.querySelector(
                    `[data-testid="song-row-${songs.indexOf(song)}"]`
                );
                if (row && window.__lenis) {
                    window.__lenis.scrollTo(row, { offset: -160, duration: 1.2 });
                }
            },
            isRest ? 650 : 0
        );
    };

    useEffect(() => {
        const handler = (e) => {
            const song = songs.find((s) => s.id === e.detail);
            if (song) playSong(song);
        };
        window.addEventListener("play-song", handler);
        return () => window.removeEventListener("play-song", handler);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <section id="canciones" data-testid="songs-section" className="relative py-24 sm:py-36 bg-paper overflow-hidden">
            <div className="max-w-5xl mx-auto px-5 sm:px-8">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center mb-14 sm:mb-20">
                    <div className="lg:col-span-7">
                        <Reveal>
                            <p className="font-mono text-xs uppercase tracking-[0.3em] text-crimson">
                                05 — Canciones
                            </p>
                        </Reveal>
                        <Reveal delay={0.1}>
                            <h2 className="mt-4 font-display text-3xl sm:text-5xl font-light tracking-tight leading-[1.05] text-ink">
                                Canciones
                            </h2>
                        </Reveal>
                        <Reveal delay={0.2}>
                            <p className="mt-5 max-w-xl text-base sm:text-lg leading-relaxed text-subtle">
                                Mi otra pasión es la música, y he logrado a través de la IA
                                transformar algunos de mis poemas en canciones.
                            </p>
                        </Reveal>
                    </div>
                    <div className="lg:col-span-5">
                        <Reveal delay={0.25}>
                            <figure
                                data-testid="songs-author-photo"
                                className="max-w-md mx-auto lg:ml-auto lg:mr-0 border border-line bg-white p-3 pb-4 shadow-xl rotate-[1.5deg] hover:rotate-0 transition-transform duration-500"
                            >
                                <img
                                    src="/media/canciones-autor.webp"
                                    alt="José Luis Garcés Maldonado cantando con micrófono en mano"
                                    className="w-full aspect-[3/2] object-cover"
                                />
                                <figcaption className="mt-3 text-center font-mono text-[10px] uppercase tracking-[0.2em] text-subtle">
                                    Del poema a la canción
                                </figcaption>
                            </figure>
                        </Reveal>
                    </div>
                </div>
                <div>
                    {featuredSongs.map((song) => (
                        <SongRow
                            key={song.id}
                            song={song}
                            index={songs.indexOf(song)}
                            playing={playingId === song.id}
                            onToggle={() => toggle(song)}
                            onEnded={() =>
                                setPlayingId((cur) => (cur === song.id ? null : cur))
                            }
                            register={(el) => {
                                players.current[song.id] = el;
                            }}
                            onReadPoem={() => openPoem(song)}
                        />
                    ))}
                </div>
                <Reveal delay={0.1}>
                    <div className="mt-8 flex">
                        <button
                            data-testid="song-more-toggle"
                            onClick={toggleExpanded}
                            aria-expanded={expanded}
                            className="flex items-center gap-3 border border-ink/30 px-7 py-3.5 font-mono text-[11px] uppercase tracking-[0.25em] text-ink/80 hover:text-crimson hover:border-crimson transition-colors duration-300"
                        >
                            {expanded ? "Escuchar menos" : "Escuchar más"}
                            <ChevronDown
                                size={14}
                                className={`transition-transform duration-300 ${expanded ? "rotate-180" : ""}`}
                            />
                        </button>
                    </div>
                </Reveal>
                <AnimatePresence initial={false}>
                    {expanded && (
                        <motion.div
                            data-testid="song-more-list"
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: "auto", opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                            className="overflow-hidden"
                        >
                            <div className="pt-2">
                                {restSongs.map((song) => (
                                    <SongRow
                                        key={song.id}
                                        song={song}
                                        index={songs.indexOf(song)}
                                        playing={playingId === song.id}
                                        onToggle={() => toggle(song)}
                                        onEnded={() =>
                                            setPlayingId((cur) => (cur === song.id ? null : cur))
                                        }
                                        register={(el) => {
                                            players.current[song.id] = el;
                                        }}
                                        onReadPoem={() => openPoem(song)}
                                    />
                                ))}
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>
                <Reveal delay={0.2}>
                    <p className="mt-8 font-mono text-[10px] uppercase tracking-[0.25em] text-subtle">
                        Poemas convertidos en canciones nuevas cada temporada
                    </p>
                </Reveal>
            </div>

            <Modal
                open={!!poemActive}
                onClose={() => setPoemActive(null)}
                label={poemActive ? `Poema: ${poemActive.title}` : ""}
                testId="song-poem-modal"
            >
                {poemActive && (
                    <div className="p-8 sm:p-12">
                        <p
                            className="font-mono text-[10px] uppercase tracking-[0.28em]"
                            style={{ color: bookColors[poemActive.bookId] }}
                        >
                            {poemActive.book} · {poemActive.year}
                        </p>
                        <h3 className="mt-3 font-display text-3xl sm:text-4xl text-ink">
                            {poemActive.title}
                        </h3>
                        <div className="mt-8 font-display text-lg leading-relaxed text-ink/90">
                            {poemActive.lines.map((line, i) =>
                                line === "" ? (
                                    <div key={i} className="h-4" />
                                ) : (
                                    <p key={i}>{line}</p>
                                )
                            )}
                        </div>
                        <div className="mt-10 border-t border-line pt-6 flex flex-wrap items-center justify-between gap-4">
                            <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-subtle">
                                José Luis Garcés Maldonado
                            </p>
                            {poemActive.videoId && (
                                <button
                                    data-testid="song-poem-video-link"
                                    onClick={() => {
                                        setPoemActive(null);
                                        goToVideoPoem(poemActive.videoId);
                                    }}
                                    className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-crimson hover:text-ink transition-colors"
                                >
                                    <Video size={12} />
                                    Ver vídeo poema
                                </button>
                            )}
                        </div>
                    </div>
                )}
            </Modal>
        </section>
    );
};

export default Songs;
